/**
 * @module EngageProvider
 * @description Initializes the Engage SDK once on the client, fires the first VIEW event,
 * and fires subsequent VIEW events on every route change. This provider manages the lifecycle
 * of the Sitecore Engage SDK integration throughout the application.
 */
import { createContext, useContext, useEffect, useMemo, useState, PropsWithChildren } from 'react';
import { type Engage } from '@sitecore/engage';
import { useRouter } from 'next/router';
import { baseEngageSettings, onEngageLoaded, processCdpResponse } from 'lib/engage';
import useExperienceEditor from 'lib/sitecore/use-experience-editor';
import { AuthState } from 'lib/auth/types';
import { logInfo } from 'lib/util/errors';
import { env } from 'lib/env';
import fnNoOp from 'lib/util/no-op';

/**
 * @interface EngageCtxValue
 * @description Context value shape for the Engage SDK provider
 * @property {Engage} [engage] - The Engage SDK instance, undefined until initialized
 * @property {boolean} isReady - Indicates whether the Engage SDK is fully initialized and ready to use
 */
interface EngageCtxValue {
  engage?: Engage;
  isReady: boolean;
}

/**
 * @constant {React.Context<EngageCtxValue>} EngageCtx
 * @description React context for sharing the Engage SDK instance across the component tree
 */
const EngageCtx = createContext<EngageCtxValue>({ isReady: false });

/**
 * @function useEngage
 * @description Custom React hook to access the Engage SDK instance from any child component
 * of EngageProvider. Returns undefined if the SDK is not yet initialized or if called
 * outside of an EngageProvider context.
 * @returns {Engage | undefined} The Engage SDK instance, or undefined if not available
 * @example
 * function MyComponent() {
 *   const engage = useEngage();
 *   if (engage) {
 *     engage.event({ ... });
 *   }
 * }
 */
export const useEngage = (): Engage | undefined => {
  const context = useContext(EngageCtx);
  try {
    return context.engage;
  } catch (e) {
    logInfo('useEngage error', e);
  }
  return undefined;
};

/**
 * @typedef {Object} EngageProviderProps
 * @property {React.ReactNode} children - Child components to be wrapped by the provider
 * @property {AuthState} [authState] - Current authentication state of the user
 */
type EngageProviderProps = PropsWithChildren<{
  authState?: AuthState;
}>;

/**
 * @component EngageProvider
 * @description React provider component that manages the Sitecore Engage SDK lifecycle.
 * Handles SDK initialization, automatic page view tracking on route changes, and user
 * identity synchronization when authentication state changes.
 *
 * Features:
 * - Initializes Engage SDK once on mount (client-side only)
 * - Sends initial PAGE VIEW event on page load
 * - Automatically tracks PAGE VIEW events on route changes
 * - Syncs user identity with CDP when user signs in/out
 * - Disabled in Experience Editor mode
 * - Respects FEATURE_FLAG_ENABLE_ENGAGE_SDK feature flag
 *
 * @param {EngageProviderProps} props - Component props
 * @returns {JSX.Element} Provider component wrapping children
 *
 * @example
 * <EngageProvider authState={authState}>
 *   <App />
 * </EngageProvider>
 */
const EngageProvider = ({
  children,
  authState = { isReady: false },
}: EngageProviderProps): JSX.Element => {
  /**
   * Early return if Engage SDK feature flag is disabled.
   * NOTE: While this **TECHNICALLY** breaks the rules of hooks by introducing a conditional
   * call to useSitecoreContext, in this case it's safe because the flag is static and can
   * only be set at build time. This means it will never change between renders, so the
   * behavior is consistent.
   */
  if (env.FEATURE_FLAG_ENABLE_ENGAGE_SDK !== true) {
    return <>{children}</>;
  }

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { locale, events } = useRouter() ?? {
    locale: 'en',
    events: { on: fnNoOp, off: fnNoOp },
  };

  /** @type {[Engage | undefined, Dispatch<SetStateAction<Engage | undefined>>]} State holding the initialized Engage SDK instance */
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [engage, setEngage] = useState<Engage>();

  /** @type {boolean} isExperienceEditor - True if currently in Sitecore Experience Editor mode */
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const isExperienceEditor: boolean = useExperienceEditor();

  /** @type {[string | undefined, Function]} State tracking the currently signed-in user's email */
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [signedInUserInState, setSignedInUserInState] = useState<string>();

  /**
   * Effect: Initialize Engage SDK once on component mount
   * - Loads the Engage SDK asynchronously
   * - Sends initial PAGE VIEW event after SDK is ready
   * - Disabled in Experience Editor mode
   * - Sets up cleanup to prevent state updates after unmount
   */
  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    let cancelled = false;
    const fnCancel = () => {
      cancelled = true;
    };
    // Handle Edit and Preview modes as desired
    // NOTE: I'm not sure what "as desired" is in this context,
    // that's why we paid the "experts", but is what it is for now.
    // XP editor doesn't work well with engage enabled, so disabling for
    // now; we can re-assess later.
    if (isExperienceEditor || engage) {
      return fnCancel;
    }
    (async () => {
      try {
        const sdk = await onEngageLoaded();
        if (!sdk) {
          logInfo('Engage SDK not initialized');
          cancelled = true;
        } else {
          logInfo('Engage SDK initialized', sdk);
        }
        // First VIEW event for a page load or hard refresh
        // We definitely don't want to be reporting page hits in edit, however
        if (cancelled || !sdk) {
          return;
        }
        await processCdpResponse(
          'pageLoad',
          sdk.pageView({
            channel: baseEngageSettings.channel,
            currency: baseEngageSettings.currency,
            referrer: document?.referrer ? document.referrer : undefined,
            page: window?.location?.pathname,
          })
        );
        if (!cancelled) {
          setEngage((current) => {
            return current === sdk ? current : sdk;
          });
          logInfo('Engage SDK loaded.');
        }
      } catch (err) {
        logInfo('Engage SDK initialization failed', err);
        cancelled = true;
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [engage, isExperienceEditor]);

  /**
   * Effect: Handle route changes and authentication state changes
   * - Sends identity events when user signs in/out
   * - Sends PAGE VIEW events on route changes
   * - Synchronizes user identity with CDP
   * - Subscribes to Next.js router events
   * Dependencies: engage, events, locale, authState, signedInUserInState
   */
  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    /**
     * @function handleRoute
     * @description Handler for route change events. Manages identity sync and page view tracking.
     */
    const handleRoute = () => {
      if (engage) {
        if (authState.isReady) {
          if (authState.isAuthenticated) {
            if (signedInUserInState !== authState.email) {
              logInfo(`Sending CDP identity event for user ${authState.email}`);
              const fullName = authState.name ?? '';
              /** @type {string} firstName - Extracted first name (substring before the first space) */
              const firstName = fullName.substring(0, fullName.indexOf(' '));
              /** @type {string} lastName - Extracted last name (substring after the first space) */
              const lastName = fullName.substring(fullName.indexOf(' ') + 1).trim() ?? '';
              // Send identity event...
              processCdpResponse(
                'engage.identify',
                engage.identity(
                  {
                    email: authState.email,
                    firstName: firstName,
                    lastName: lastName,
                    channel: baseEngageSettings.channel,
                    currency: baseEngageSettings.currency,
                    page: window?.location?.pathname,
                    pointOfSale: baseEngageSettings.pointOfSale,
                    // language: locale,
                    identifiers: [{ provider: 'email', id: authState.email }],
                  },
                  {
                    authData: authState.claims?.authz_center?.join(',') ?? '',
                  }
                )
              );
              setSignedInUserInState(authState.email);
            }
          } else if (signedInUserInState !== undefined) {
            logInfo(`Clearing session association with signed-out user ${signedInUserInState}`);
            processCdpResponse(
              'engage.identify',
              engage.identity(
                {
                  email: '',
                  channel: baseEngageSettings.channel,
                  currency: baseEngageSettings.currency,
                  pointOfSale: baseEngageSettings.pointOfSale,
                  identifiers: [{ provider: 'email', id: '' }],
                },
                {
                  authData: 'remove:true',
                }
              )
            );
            setSignedInUserInState(undefined);
          }
        }
        // Note here we could do some magic to de-identify a long-running cdp session
        // but currently keeping it simple and using short-lived cdp cookies

        // Fire VIEW on all client-side route changes
        engage
          .pageView({
            channel: baseEngageSettings.channel,
            pointOfSale: baseEngageSettings.pointOfSale,
            currency: baseEngageSettings.currency,
            ...{
              page: window?.location?.pathname ? window.location.pathname : undefined,
              referrer: document?.referrer ? document.referrer : undefined,
            },
            // language: locale,
          })
          .then((v) => {
            logInfo(`Engage SDK pageView event sent on route change: ${JSON.stringify(v)}`);
          });
      }
    };

    if (engage) {
      events.on('routeChangeComplete', handleRoute);
    }

    return () => {
      events.off('routeChangeComplete', handleRoute);
    };
  }, [engage, events, locale, authState, signedInUserInState]);

  /**
   * Memoized context value to prevent unnecessary re-renders of consumer components.
   * Only updates when engage instance or authState.isReady changes.
   * @type {EngageCtxValue}
   */
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const ctxValue: EngageCtxValue = useMemo(
    () => ({ engage, isReady: !!engage && authState.isReady }),
    [engage, authState.isReady]
  );
  return <EngageCtx.Provider value={ctxValue}>{children}</EngageCtx.Provider>;
};

export default EngageProvider;
